// hospitality.tsx — r01 Mehmonxona preview card.
//
// "Palov & Co" — a fictional Tashkent restaurant grand opening. The aim is
// hospitality industry sophistication: Soho House × an Uzbek choyxona. We
// stay on a cream-paper backdrop, frame the food photo in chocolate brown
// with brass corner accents, and set the restaurant name in Marcellus.
//
// Visual layers (back to front):
//   1. Cream paper noise — a low-opacity SVG fiber texture that reads as
//      printed menu stock at thumbnail size.
//   2. A 1px chocolate-brown geometric border, inset from the card edge.
//      Each corner gets a small brass L-bracket — the kind of mark you see
//      on a stamped reservation card.
//   3. "GRAND OPENING" eyebrow in brass small-caps, centered up top.
//   4. The food photograph, framed in chocolate brown with a subtle inner
//      shadow so it sits like a tipped-in menu plate.
//   5. The restaurant name in large Marcellus, then the Uzbek tagline in
//      brass italic.
//   6. A bottom keyline carrying the date · venue, with crossed brass
//      cutlery silhouettes (fork + knife) flanking it.
//
// All sizing is %/em-based so the same component renders identically as a
// catalog tile, an editor preview, and the printed card.

import { Photo, type LayoutCtx } from "./_shared";

// Subtle paper-fiber noise overlay — short angled fiber strokes in the
// chocolate ink at very low opacity. Reads as cream menu stock rather than
// any visible texture.
const PAPER_FIBER = (ink: string) =>
  `url("data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'>` +
      `<g stroke='${ink}' stroke-width='0.6' opacity='0.10'>` +
      `<line x1='8'   y1='14'  x2='22' y2='17'/>` +
      `<line x1='38'  y1='8'   x2='52' y2='12'/>` +
      `<line x1='72'  y1='20'  x2='84' y2='18'/>` +
      `<line x1='100' y1='10'  x2='110' y2='15'/>` +
      `<line x1='14'  y1='44'  x2='26' y2='42'/>` +
      `<line x1='48'  y1='52'  x2='62' y2='55'/>` +
      `<line x1='86'  y1='48'  x2='98' y2='44'/>` +
      `<line x1='6'   y1='74'  x2='18' y2='78'/>` +
      `<line x1='40'  y1='82'  x2='54' y2='80'/>` +
      `<line x1='74'  y1='90'  x2='88' y2='92'/>` +
      `<line x1='102' y1='78'  x2='114' y2='82'/>` +
      `<line x1='22'  y1='106' x2='34' y2='110'/>` +
      `<line x1='58'  y1='112' x2='70' y2='108'/>` +
      `<line x1='90'  y1='110' x2='104' y2='112'/>` +
      `</g></svg>`,
  )}")`;

// One brass L-bracket placed at a corner. The `corner` prop rotates it.
function CornerBracket({
  size,
  color,
  corner,
}: {
  size: number;
  color: string;
  corner: "tl" | "tr" | "bl" | "br";
}) {
  const rot = corner === "tl" ? 0 : corner === "tr" ? 90 : corner === "br" ? 180 : 270;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      style={{ display: "block", transform: `rotate(${rot}deg)` }}
      aria-hidden
    >
      {/* Outer L */}
      <path d="M 2 2 L 22 2 M 2 2 L 2 22" stroke={color} strokeWidth="1.6" fill="none" />
      {/* Inner hairline rule */}
      <path d="M 5 5 L 18 5 M 5 5 L 5 18" stroke={color} strokeWidth="0.6" fill="none" opacity="0.7" />
      {/* Tiny diamond stud at the elbow */}
      <path d="M 2 2 L 5 0 L 8 2 L 5 4 Z" fill={color} opacity="0.85" />
    </svg>
  );
}

// Crossed fork + knife silhouette in brass. Tiny, sits at either end of the
// bottom keyline.
function Cutlery({ color, flip = false }: { color: string; flip?: boolean }) {
  return (
    <svg
      viewBox="0 0 32 32"
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid meet"
      style={{ display: "block", transform: flip ? "scaleX(-1)" : undefined }}
      aria-hidden
    >
      {/* Fork — three short tines, a stem, and a slight handle taper */}
      <g transform="rotate(-22 16 16)" stroke={color} strokeLinecap="round" fill="none">
        <line x1="11" y1="3" x2="11" y2="9" strokeWidth="1" />
        <line x1="13.5" y1="3" x2="13.5" y2="9" strokeWidth="1" />
        <line x1="16" y1="3" x2="16" y2="9" strokeWidth="1" />
        <line x1="13.5" y1="9" x2="13.5" y2="29" strokeWidth="1.6" />
      </g>
      {/* Knife — flat blade tapering to point, narrow handle */}
      <g transform="rotate(22 16 16)" stroke={color} fill={color}>
        <path
          d="M 18 3 Q 21 5 21 14 L 19 14 L 19 29 L 17 29 L 17 14 L 16 14 Q 16 5 18 3 Z"
          strokeWidth="0.6"
          opacity="0.9"
        />
      </g>
    </svg>
  );
}

export function HospitalityLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
  photos,
}: LayoutCtx) {
  const heroPhoto = photo || photos[0];
  // Slightly warmer cream for the photo frame inset than the page bg.
  const frameInk = ink;
  const brass = accent;

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: bg,
        color: ink,
        overflow: "hidden",
        padding: "6%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      {/* Cream paper-fiber overlay */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: PAPER_FIBER(ink),
          backgroundSize: "120px 120px",
          opacity: 0.85,
          pointerEvents: "none",
        }}
      />

      {/* Chocolate-brown geometric border, inset from edge */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: "4%",
          border: `1px solid ${frameInk}`,
          pointerEvents: "none",
        }}
      />

      {/* Brass corner brackets */}
      <div aria-hidden style={{ position: "absolute", inset: "4%", pointerEvents: "none" }}>
        <span style={{ position: "absolute", top: -1, left: -1 }}>
          <CornerBracket size={18} color={brass} corner="tl" />
        </span>
        <span style={{ position: "absolute", top: -1, right: -1 }}>
          <CornerBracket size={18} color={brass} corner="tr" />
        </span>
        <span style={{ position: "absolute", bottom: -1, left: -1 }}>
          <CornerBracket size={18} color={brass} corner="bl" />
        </span>
        <span style={{ position: "absolute", bottom: -1, right: -1 }}>
          <CornerBracket size={18} color={brass} corner="br" />
        </span>
      </div>

      {/* Eyebrow — GRAND OPENING in brass small-caps */}
      <div
        style={{
          position: "relative",
          fontFamily: "var(--font-ui)",
          fontSize: 7,
          fontWeight: 700,
          letterSpacing: "0.42em",
          color: brass,
          marginTop: "3%",
          marginBottom: "5%",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span style={{ width: 14, height: 1, background: brass }} />
        GRAND OPENING
        <span style={{ width: 14, height: 1, background: brass }} />
      </div>

      {/* Food photograph framed in chocolate brown */}
      <div
        style={{
          position: "relative",
          width: "78%",
          aspectRatio: "5 / 4",
          padding: 4,
          background: frameInk,
          boxShadow: `0 6px 14px ${ink}33`,
        }}
      >
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            overflow: "hidden",
            background: bg,
          }}
        >
          <Photo
            src={heroPhoto}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
            fallback={
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: `radial-gradient(circle at 50% 45%, ${brass}55, ${frameInk})`,
                }}
              />
            }
          />
          {/* Inner brass hairline — the menu plate border */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 4,
              border: `1px solid ${brass}88`,
              pointerEvents: "none",
            }}
          />
          {/* Subtle warm wash to keep the dish on-palette */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background: `linear-gradient(180deg, ${brass}10 0%, transparent 35%, ${ink}26 100%)`,
              mixBlendMode: "multiply",
            }}
          />
        </div>
      </div>

      {/* Restaurant name in Marcellus */}
      <div
        style={{
          position: "relative",
          fontFamily: font,
          fontWeight: 400,
          fontSize: 30,
          letterSpacing: "0.04em",
          marginTop: "5%",
          color: frameInk,
          lineHeight: 1.05,
        }}
      >
        {names}
      </div>

      {/* Brass tagline */}
      <div
        style={{
          position: "relative",
          fontFamily: font,
          fontStyle: "italic",
          fontSize: 11,
          letterSpacing: "0.06em",
          marginTop: 8,
          color: brass,
        }}
      >
        Yangi Toshkent palovi
      </div>

      {/* Bottom keyline — date · venue with cutlery flanks */}
      <div
        style={{
          position: "relative",
          marginTop: "auto",
          marginBottom: "1%",
          width: "78%",
          display: "grid",
          gridTemplateColumns: "auto 1fr auto",
          alignItems: "center",
          gap: 12,
        }}
      >
        <span aria-hidden style={{ width: 18, height: 18, opacity: 0.85 }}>
          <Cutlery color={brass} />
        </span>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span
            aria-hidden
            style={{ display: "block", width: "100%", height: 1, background: brass, opacity: 0.65 }}
          />
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 6.5,
              fontWeight: 700,
              letterSpacing: "0.36em",
              color: frameInk,
              fontFeatureSettings: '"tnum" 1, "lnum" 1',
            }}
          >
            {date.replace(/\./g, " · ")} · {venue.toUpperCase()}
          </div>
          <span
            aria-hidden
            style={{ display: "block", width: "100%", height: 1, background: brass, opacity: 0.65 }}
          />
        </div>
        <span aria-hidden style={{ width: 18, height: 18, opacity: 0.85 }}>
          <Cutlery color={brass} flip />
        </span>
      </div>
    </div>
  );
}
